import { useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { Coffee, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button/button";
import { getTodayBreakStatusQueryOptions } from "@/config/queries/emp-query-options";
import { useBreakActions } from "@/components/dash-nav/break-control/use-break-actions";
import { useElapsedSeconds } from "@/components/break/use-elapsed";
import { formatClock } from "@/components/break/format";
import { formatTimeToIST } from "@/utils/timezone";

/** Shown under the navbar on every employee page while a break is open. */
export const OnBreakBanner = () => {
  const key = Cookies.get("key") as string;
  const { data } = useQuery(getTodayBreakStatusQueryOptions(key));
  const { endBreak, isEnding } = useBreakActions();
  const elapsed = useElapsedSeconds(data?.openBreak?.startedAt ?? null);

  if (!data?.onBreak || !data.openBreak) return null;

  return (
    <div
      className="mx-5 mt-4 p-4 rounded-lg border-2 border-amber-500 bg-amber-50 dark:bg-amber-950/30 flex flex-wrap items-center gap-3"
      role="status"
      data-testid="on-break-banner"
    >
      <Coffee className="h-6 w-6 text-amber-600 shrink-0" />
      <div className="flex-1 min-w-[220px]">
        <div className="text-amber-800 dark:text-amber-300 font-semibold text-lg">
          You are on a break
        </div>
        <p className="text-amber-700 dark:text-amber-200">
          Since <span className="font-semibold">{formatTimeToIST(data.openBreak.startedAt)}</span>
          {data.openBreak.reason ? <> · {data.openBreak.reason}</> : null}
          {" · "}
          <span className="font-mono" data-testid="on-break-elapsed">
            {formatClock(elapsed)}
          </span>{" "}
          so far. Press Resume to continue working.
        </p>
      </div>
      <Button
        onClick={() => endBreak()}
        disabled={isEnding}
        className="gap-2 bg-amber-500 hover:bg-amber-600 text-black"
        data-testid="banner-resume-button"
      >
        {isEnding && <Loader2 className="w-3 h-3 animate-spin" />}
        Resume
      </Button>
    </div>
  );
};
